import { Component, OnInit, Input } from '@angular/core';
import { DeviceService } from '../../device.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from 'src/app/users/users.service';
import * as moment from 'moment';
import { DeviceFactory } from '../../device-factory/device-factory';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { DeviceTypeService } from '../../device-type.service';
import { SitesService } from 'src/app/sites/sites.service';
import { SiteTemperatureUnit } from '../../../site-settings/site-utilization-settings/site-utilization-settings.interface';

@Component({
	selector: 'app-daily-details',
	templateUrl: './daily-details.component.html',
	styleUrls: ['./daily-details.component.css']
})
export class DailyDetailsComponent implements OnInit {

	@Input() device: any = {};
	@Input() currentSite: any;
	@Input() dateRange: any = {};
	@Input() invalidDateRange: boolean = false;
	@Input() studyId: number = 0;
	@Input() studyInfo: any = {};

	booleanFields = [
		'is_complete',
		'temperature_sensor_enabled',
		'sensor_issue',
		'missed_fi',
		'watered',
		'has_eq',
	];

	floatFields = [
		'charge_events_ahr',
		'charge_events_kwhr',
		'charge_kwhr_ld',
		'charge_kwhr_hd',
		'inuse_events_ahr',
		'inuse_events_kwhr',
		'max_temperature_value',
		'min_temperature_value',
		'deep_discharge_value',
		'inuse_max_current',
		'charge_max_current',
		'billed_kwh',
		'ah_h',
		'event_generated_energy',
		'min_voltage',
		'max_voltage',
		'min_voltage_ll',
		'max_voltage_ll',
		'negative_peak_power',
		'positive_peak_power',
		'event_consumed_energy',
		'min_current',
		'max_current',
		'event_consumed_kvarh',
		'event_generated_kvarh'
	];

	integerFields = [
		'total_charge_events',
		'total_inuse_events',
		'total_idle_events',
		'min_soc',
		'max_temperature_exceeded',
		'min_temperature_exceeded',
		'deep_discharge_exceeded',
		'potential_week_cells_exceeded',
		'charge_oppurtinity',
		'ebu',
		'ah_h',
		'cooldowns',
		'water_low_days',
		'latitude',
		'longitude',
	];

	hiddenFields = [
		'is_complete',
		'charge_kwhr_ld',
		'charge_kwhr_hd',
		'charge_ahr',
		'min_soc',
		'max_temperature_value',
		'min_temperature_value',
		'max_temperature_exceeded',
		'min_temperature_exceeded',
		'deep_discharge_value',
		'deep_discharge_exceeded',
		'potential_week_cells_exceeded',
		'inuse_max_current',
		'charge_max_current',
		'charge_oppurtinity',
		'sensor_issue',
		'temperature_sensor_enabled',
		'idle_times_distribution',
		'non_plugged_in_idle_times',
		'events_ids',
		'charge_oppurtinity_duration',
		'water_level_low',
		'watered',
		'cooldowns',
		'water_low_days',
		'latitude',
		'longitude',
	];

	longFieldNames = [
		'potential_week_cells_exceeded',
		'charge_oppurtinity_duration',
	];

	durationFields = [
		"idle_duration",
		"charge_oppurtinity_duration",
		"charge_duration",
		"inuse_duration",
		"accurate_inuse_seconds",
		"accurate_charge_seconds",
		"total_off_time",
		"lift_time",
		"travel_time",
		'events_duration',
		'consumed_energy_duration',
		'generated_energy_duration',
	];

	dateFields = [
		"date"
	];

	NocAccessFields = [
		'billed_kwh'
	]

	helpFields = [
		'date',
		'accurate_inuse_seconds',
		'total_off_time',
		'total_charge_events',
		'inuse_events_kwhr',
		'inuse_events_ahr',
		'total_inuse_events',
		'total_idle_events',
		'charge_duration',
		'inuse_duration',
		'inuse_max_current',
		'idle_duration',
		'charge_events_ahr',
		'charge_events_kwhr',
	];

	temperatureFields = [
		"min_temperature_value",
		"max_temperature_value"
	];

	temperatureUnit: number = SiteTemperatureUnit.FAHRENHEIT;
	dailyDetailsRawData: any;

	imVoltageFields: string [] = [
		'max_voltage',
		'min_voltage'
	];

	data: any[] = [];
	hasGrid: boolean = false;

	columnDefs: any[] = [];
	rowData: any[] = [];

	currentUser: any = {};

	allColumnsKeys: string[] = [
		"date",
		"accurate_inuse_seconds",
		"accurate_charge_seconds",
		"total_off_time",
		"is_complete",
		'charge_kwhr_ld',
		'charge_kwhr_hd',
		"charge_events_ahr",
		"charge_events_kwhr",
		"inuse_events_ahr",
		"inuse_events_kwhr",
		"billed_kwh",
		"total_charge_events",
		"total_inuse_events",
		"charge_duration",
		"inuse_duration",
		"total_idle_events",
		"min_soc",
		"max_temperature_value",
		"min_temperature_value",
		"max_temperature_exceeded",
		"min_temperature_exceeded",
		"deep_discharge_value",
		"deep_discharge_exceeded",
		"potential_week_cells_exceeded",
		"inuse_max_current",
		"charge_max_current",
		"charge_oppurtinity",
		"sensor_issue",
		"temperature_sensor_enabled",
		"idle_times_distribution",
		"non_plugged_in_idle_times",
		"events_ids",
		"idle_duration",
		"charge_oppurtinity_duration",
		"water_level_low",
		"lift_time",
		"travel_time",
		"ebu",
		"ah_h",
		"has_eq",
		"missed_fi",
		"watered",
		"cooldowns",
		"water_low_days",
		"latitude",
		"longitude",
	];

	columnsWithComma = [
		"idle_times_distribution",
		"non_plugged_in_idle_times",
		"events_ids"
	];

	imDeviceColumnKey: string[] = [
		'date',
		'events_duration',
		'event_generated_energy',
		'min_voltage',
		'max_voltage',
		'min_voltage_ll',
		'max_voltage_ll',
		'max_current',
		'consumed_energy_duration',
		'generated_energy_duration',
		'event_consumed_energy',
		'positive_peak_power',
		'negative_peak_power',
		'event_consumed_kvarh',
		'event_generated_kvarh'
	];

	waterFields: string[] = [
		'water_level_low',
		'watered',
		'water_low_days'
	];

	allTemperatureFields: string[] = [
		"min_temperature_value",
		"max_temperature_value",
		"max_temperature_exceeded",
		"min_temperature_exceeded",
		"temperature_sensor_enabled",
		"cooldowns",
	];

	eqFields: string[] = [
		'has_eq',
		'missed_fi'
	];

	absFields: string[] = [
		'max_voltage_ll',
		'min_voltage_ll'
	];

	/**
	 * ST-2587 - the daily minimum that decides whether each IoTEmeter column is shown as N/A. The
	 * rollup in DAO/imDailyDetailsDAO.js leaves a minimum out only when none of the day's events took
	 * that reading, and its paired maximum is then a meaningless 0 - so judge both by the minimum.
	 */
	readonly noReadingFlagByField: Record<string, string> = {
		min_voltage: 'min_voltage',
		max_voltage: 'min_voltage',
		min_voltage_ll: 'min_voltage_ll',
		max_voltage_ll: 'min_voltage_ll',
		max_current: 'min_current',
	};

	// table variables
	columnConfig: ColumnsConfig[] = [];
	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 100,
		fitScreen: true,
		draggable: true,
		fileName: 'daily_details' + '_' + this.device.mac_address,
		tableId: 'daily-details-table-' + this.currentUser.id,
		csvFileHeaderInfo: []
	};

	constructor(
		private deviceService: DeviceService,
		private commonService: CommonService,
		private translateService: TranslateService,
		private usersService: UsersService,
		private deviceTypeService: DeviceTypeService,
		private siteService: SitesService,
	) { }

	ngOnInit() {
		this.tableConfig.fileName = 'daily_details' + '_' + this.device.mac_address;

		this.siteService.getSiteUtilizationSettings(this.currentSite.id).subscribe((res: any) => {
			const siteUtilizationSettings = res?.[0];
			if (!siteUtilizationSettings)
				return;

			const batteriesSettings = siteUtilizationSettings.batteries_utilization_settings;
			this.temperatureUnit = batteriesSettings.temperature_unit ?? SiteTemperatureUnit.FAHRENHEIT;
			if (this.dailyDetailsRawData) {
				this.fillTableData(this.dailyDetailsRawData);
				this.setTableColumnsConfig();
			}
		});

		if (this.studyInfo && this.studyInfo.id)
			this.commonService.setPageTabTitle(`${this.translateService.instant('studies.power_studies')} - ${this.studyInfo.name} - ${this.translateService.instant('nav.daily_details')}`);
		else
			this.commonService.setPageTabTitle(`${this.currentSite.customer_name} - ${this.currentSite.name} - ${this.device.serial_number} - ${this.translateService.instant('nav.daily_details')}`);

		this.usersService.currentUser.subscribe((user: any) => {
			if(user && user.id) {
				this.currentUser = user;
				this.tableConfig.tableId = 'daily-details-table-' + this.currentUser.id;
			}

			this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
				customerName: this.currentSite.customer_name,
				siteName: this.currentSite.name,
				deviceSN: this.device.serial_number,
				includeSpacing: false
			});
		});
	}

	getData() {
		let fromDateRange = this.dateRange.fromDate;
		let toDateRange = this.dateRange.toDate;

		if (!this.device || !this.device.mac_address || !fromDateRange || !toDateRange) return;

		let zoneDiff		= new Date().getTimezoneOffset() * -1;
		let fromDate:any	= new Date(new Date(fromDateRange).getTime() + (zoneDiff * 60 * 1000));
		let toDate:any		= new Date(new Date(toDateRange).getTime() + (zoneDiff * 60 * 1000));
		fromDate			= moment(fromDate).utc().startOf('day').unix();
		toDate				= moment(toDate).utc().endOf('day').unix();

		this.deviceService.getDailyDetails(this.device.mac_address, fromDate, toDate, {}, this.studyId).subscribe((data: any) => {
			this.dailyDetailsRawData = data;
			this.fillTableData(data);
			this.setTableColumnsConfig();
		});
	}

	setTableColumnsConfig() {
		const deviceConfig = DeviceFactory.createDevice(this.device.config_info.sct_device_type);
		const dailyDetailsConfig = deviceConfig.config.dailyDetails;
		const fieldsToHide = Object.keys(dailyDetailsConfig.hasField);
		const isIMDevice = DeviceFactory.isIMDeviceType(this.device.config_info.sct_device_type);
		let allDailyDetailsColumns = isIMDevice ? this.imDeviceColumnKey : this.allColumnsKeys;
		allDailyDetailsColumns = this.filterFields(allDailyDetailsColumns, deviceConfig);

		if(this.tableData.length === 0)
			return;

		let gridColumns		= [];
		let existsColumns	= Object.keys(this.tableData[0]);

		const psOrHasFields = Object.keys(dailyDetailsConfig.hasOrPowerStudy);

		if (isIMDevice) {
			for (const field of this.imVoltageFields) {
				if (this.device?.config_info?.voltage_reference == this.deviceService.lineToLine) 
					allDailyDetailsColumns = allDailyDetailsColumns.filter((item) => item !== field)
				else 
					allDailyDetailsColumns = allDailyDetailsColumns.filter((item) => item !== field + '_ll')
			}
		}

		for(let field of allDailyDetailsColumns) {
			if (!this.usersService.hasAccessPermission(null, `daily_details.${field}`, 'read', null, deviceConfig.config.permissionMapperKey))
				continue;

			let columnConfig: ColumnsConfig = {key: field, name: this.translateService.instant('daily_details.'+field), type: 'string'};

			if (this.temperatureFields.includes(field))
				columnConfig.name = `${columnConfig.name} (${this.commonService.tempUnitSymbol(this.temperatureUnit)})`;

			if (field === 'water_level_low') {
				columnConfig.name = this.translateService.instant('daily_details.water_level');
			}
			if(fieldsToHide.includes(field) && dailyDetailsConfig.hasField[field] === false)
				continue;

			if(psOrHasFields.includes(field) && !this.deviceTypeService.isStudyDevice(this.device.config_info) && !dailyDetailsConfig.hasOrPowerStudy[field])
				continue;

			if(!existsColumns.includes(field))
				continue;
			if (field === 'water_level_low') {
				columnConfig.type = 'string';
				columnConfig.filterType = 'dropdown';
				columnConfig.items = [
					{ label: 'Low', value: 'Low' },
					{ label: 'High', value: 'High' }
				];
			} else if(this.booleanFields.includes(field)) {
				columnConfig.type = 'boolean';
			} else if(this.integerFields.includes(field) || this.floatFields.includes(field)) {
				columnConfig.type = 'number';
			} else if(this.durationFields.includes(field)) {
				columnConfig.type = 'duration';
				columnConfig.key = field+"_formatted";
			} else if(this.dateFields.includes(field)) {
				columnConfig.type = 'date';
			}

			if(this.hiddenFields.includes(field))
				columnConfig.hidden = true;

			if(this.NocAccessFields.includes(field) && !this.usersService.userHasNOCAccess())
				continue;

			if(this.helpFields.includes(field))
				columnConfig.hasTooltip = this.translateService.instant('grid_help.daily_details.'+field);

			gridColumns.push(columnConfig);
		}

		this.columnConfig = gridColumns;
		this.hasGrid = existsColumns.length > 0;
	}

	fillTableData(data) {
	 	const deviceConfig = DeviceFactory.createDevice(this.device.config_info.sct_device_type);
		const permissionMapperKey = deviceConfig.config.permissionMapperKey;
		const dailyDetailsConfig = deviceConfig.config.dailyDetails;
		const isIMDevice = DeviceFactory.isIMDeviceType(this.device.config_info.sct_device_type);

		let floatFields = [
			'deep_discharge_value',
			'inuse_max_current',
			'charge_max_current',
			...this.floatFields
		];
		let dailyDetails = [];
		for(let elementIdx in data) {
			let rowData: TableData = {};
			let dayData = data[elementIdx];
			if(!dayData || !dayData.date)
				continue;

			if(dailyDetailsConfig.useBilledKwh && ![this.commonService.usaStates.oregon, this.commonService.usaStates.california].includes(this.currentSite.state_id))
				dayData.charge_events_kwhr = dayData.billed_kwh;

			if (dailyDetailsConfig.hasField.ebu && deviceConfig.config.fields.batteryCapacity && this.usersService.hasAccessPermission(null, 'battlink_admin', 'read', null, permissionMapperKey))
				rowData.ebu = { value: dayData.inuse_ahr ? (dayData.inuse_ahr / (this.device.config_info.battery_capacity * 0.8))?.toFixed(2) : 0 };

			if (dailyDetailsConfig.hasField.ah_h && this.usersService.hasAccessPermission(null, 'battlink_admin', 'read', null, permissionMapperKey)) {
				const hasInuseAhr = dayData.inuse_ahr && dayData.inuse_duration;
				const inuseAhr = hasInuseAhr ? (dayData.inuse_ahr / (dayData.inuse_duration / 3600))?.toFixed(2) : 0;
				rowData.ah_h = { value: inuseAhr };
			}

			Object.keys(dayData).forEach((field) => {
				switch(true) {
					case this.dateFields.includes(field):
						rowData[field] = { value: moment(dayData[field]*1000).utc().format('MM/DD/YYYY'), sortValue: dayData[field]*1000 };
						break;
					case this.temperatureFields.includes(field):
						rowData[field] = { value: this.commonService.formatSensorTemp(dayData[field], this.temperatureUnit) };
						break;
					case this.durationFields.includes(field):
						const value = dayData[field] && dayData[field] > 0 ? dayData[field] : 0;
						rowData[field +"_formatted"] = { value: this.commonService.timeFormat(value, {limitToOneDay: true}), sortValue: value || 0 };
						rowData[field] = value || 0;
						break;
					case floatFields.includes(field):
						rowData[field] = { value: +(dayData[field]/1.0).toFixed(1) };
						break;
					case this.columnsWithComma.includes(field):
						rowData[field] = { value: `${dayData[field]}` };
						break;
					case this.absFields.includes(field):
						rowData[field] = { value: Math.abs(dayData[field]) };
						break;
					case field === 'water_level_low':
						if (dayData[field] === 'N/A' || dayData[field] === null || dayData[field] === undefined) {
							rowData[field] = { value: '-' };
						} else {
							rowData[field] = { value: dayData[field] ? 'Low' : 'High' };
						}
						break;
					default:
						rowData[field] = { value: dayData[field]};
						break;
				}
			});

			if (isIMDevice)
				for (const field of Object.keys(this.noReadingFlagByField))
					if (this.hasNoDailyReading(dayData[this.noReadingFlagByField[field]]))
						rowData[field] = { value: this.commonService.NO_READING, sortValue: null };

			if (!dailyDetailsConfig.hasField.eq || !this.usersService.hasAccessPermission(null, 'battlink_admin', 'read', null, permissionMapperKey))
				delete rowData['has_eq'];

			if (!dailyDetailsConfig.hasField.missed_fi || !this.usersService.hasAccessPermission(null, 'battlink_admin', 'read', null, permissionMapperKey))
				delete rowData['missed_fi'];

			if(dayData)
				dailyDetails.push(rowData);
		}

		dailyDetails = this.getFilledDailyDetails(dailyDetails);
		this.tableData = dailyDetails;
	}

	ngOnChanges() {
		this.getData();
	}

	getFilledDailyDetails(dailyDetails) {
		const allDailyDetailsColumns = DeviceFactory.isIMDeviceType(this.device.config_info.sct_device_type) ? this.imDeviceColumnKey : this.allColumnsKeys;
		dailyDetails = dailyDetails.map(row => {
			const defaultRaw = { ...row };
			allDailyDetailsColumns.forEach(key => {
				if (!(key in defaultRaw)) 
					defaultRaw[key] = { value: '-' };
			});
			return defaultRaw;
		});
		return dailyDetails;
	}

	private hasNoDailyReading(value: any): boolean {
		return value === undefined || value === null || this.commonService.hasNoReading(value);
	}

	private filterFields(columns: string[], deviceConfig: any): string[] {
		if (!deviceConfig) return columns;

		if (deviceConfig.config.fields.hasTemperatureSensor === false) {
			columns = columns.filter((item) => !this.allTemperatureFields.includes(item));
		}

		if (deviceConfig.config.fields.hasWaterLevel === false || this.device.config_info.has_water_level === false) {
			columns = columns.filter((item) => !this.waterFields.includes(item));
		}

		if (!deviceConfig.config.dailyDetails.hasField.eq) {
			columns = columns.filter((item) => !this.eqFields.includes(item));
		}

		return columns;
	}
}
